import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { PageHead } from "@/components/Filters";
import { api, errMsg } from "@/lib/api";

const QUOTAS = [
  "Govt Quota",
  "Merit-Private",
  "Merit Private - Minority",
  "Merit Open",
  "Management",
  "NRI Quota",
];

export default function AdminMappings() {
  const [body, setBody] = useState("KEA");
  const [rows, setRows] = useState([]);
  const [suffixes, setSuffixes] = useState([]);
  const [form, setForm] = useState({ raw_code: "", quota: QUOTAS[0], category: "" });
  const [sform, setSform] = useState({ code: "", label: "" });

  const load = async (b = body) => {
    try {
      const [m, s] = await Promise.all([
        api.get("/admin/category-mappings", { params: { counselling_body: b } }),
        api.get("/admin/category-suffixes", { params: { counselling_body: b } }),
      ]);
      setRows(m.data);
      setSuffixes(s.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  useEffect(() => {
    load(body);
  }, [body]);

  const save = async () => {
    if (!form.raw_code || !form.category) return toast.error("Raw code and category are required");
    try {
      await api.post("/admin/category-mappings", { counselling_body: body, ...form });
      toast.success("Mapping saved");
      setForm({ raw_code: "", quota: QUOTAS[0], category: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (raw_code) => {
    await api.delete("/admin/category-mappings", { params: { counselling_body: body, raw_code } });
    toast.success("Mapping deleted");
    load();
  };

  const saveSuffix = async () => {
    if (!sform.code || !sform.label) return toast.error("Suffix code and label are required");
    try {
      await api.post("/admin/category-suffixes", { counselling_body: body, ...sform });
      toast.success("Suffix rule saved");
      setSform({ code: "", label: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const removeSuffix = async (code) => {
    await api.delete("/admin/category-suffixes", { params: { counselling_body: body, code } });
    toast.success("Suffix deleted");
    load();
  };

  return (
    <div className="space-y-5" data-testid="admin-mappings-page">
      <PageHead
        title="Category mapping rules"
        subtitle="State-specific mapping packs. Karnataka rules never apply to MCC or other bodies. A raw code is resolved by exact rule first, then by base code + suffix (e.g. 3A + G = 3AG)."
      />

      <div className="flex flex-wrap items-end gap-3 border border-slate-200 bg-white p-4">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Counselling body</span>
          <select
            data-testid="mapping-body-select"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="h-9 border border-slate-300 px-2 text-sm"
          >
            {["KEA", "MCC"].map((b) => (
              <option key={b}>{b}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Raw code</span>
          <input
            data-testid="mapping-raw-input"
            value={form.raw_code}
            onChange={(e) => setForm((p) => ({ ...p, raw_code: e.target.value.toUpperCase() }))}
            placeholder="GMPH"
            className="h-9 w-28 border border-slate-300 px-2 font-mono-num text-sm"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Normalized quota</span>
          <select
            data-testid="mapping-quota-select"
            value={form.quota}
            onChange={(e) => setForm((p) => ({ ...p, quota: e.target.value }))}
            className="h-9 border border-slate-300 px-2 text-sm"
          >
            {QUOTAS.map((q) => (
              <option key={q}>{q}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Normalized category</span>
          <input
            data-testid="mapping-category-input"
            value={form.category}
            onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
            placeholder="General Merit (PH)"
            className="h-9 w-56 border border-slate-300 px-2 text-sm"
          />
        </label>
        <button
          data-testid="mapping-save-btn"
          onClick={save}
          className="h-9 bg-[#1A365D] px-4 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#122845]"
        >
          Save rule
        </button>
      </div>

      <div className="overflow-auto border border-slate-200 bg-white">
        <table className="data-table" data-testid="mappings-table">
          <thead>
            <tr>
              <th>Raw code</th>
              <th>Normalized quota</th>
              <th>Normalized category</th>
              <th>Body</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="py-6 text-center text-slate-500">
                  No mapping rules for {body} yet.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.raw_code} data-testid={`mapping-row-${r.raw_code}`}>
                <td className="font-mono-num font-semibold">{r.raw_code}</td>
                <td>
                  <span className="border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[11px] text-blue-800">
                    {r.quota}
                  </span>
                </td>
                <td>{r.category}</td>
                <td className="font-mono-num">{r.counselling_body}</td>
                <td>
                  <button
                    data-testid={`delete-mapping-${r.raw_code}`}
                    onClick={() => remove(r.raw_code)}
                    className="text-slate-400 hover:text-rose-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <h2 className="font-display text-lg font-bold text-slate-900">Suffix rules</h2>
        <p className="mt-1 text-sm text-slate-500">
          Suffixes combine with a base code, so <span className="font-mono-num">3A</span> +{" "}
          <span className="font-mono-num">G</span> resolves <span className="font-mono-num">3AG</span> without needing a
          rule for every combination.
        </p>

        <div className="mt-3 flex flex-wrap items-end gap-3 border border-slate-200 bg-white p-4">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Suffix code</span>
            <input
              data-testid="suffix-code-input"
              value={sform.code}
              onChange={(e) => setSform((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
              placeholder="G"
              className="h-9 w-24 border border-slate-300 px-2 font-mono-num text-sm"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Meaning</span>
            <input
              data-testid="suffix-label-input"
              value={sform.label}
              onChange={(e) => setSform((p) => ({ ...p, label: e.target.value }))}
              placeholder="General"
              className="h-9 w-64 border border-slate-300 px-2 text-sm"
            />
          </label>
          <button
            data-testid="suffix-save-btn"
            onClick={saveSuffix}
            className="h-9 bg-[#1A365D] px-4 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#122845]"
          >
            Save suffix
          </button>
        </div>

        <div className="mt-3 overflow-auto border border-slate-200 bg-white">
          <table className="data-table" data-testid="suffixes-table">
            <thead>
              <tr>
                <th>Suffix</th>
                <th>Meaning</th>
                <th>Body</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {suffixes.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-slate-500">
                    No suffix rules for {body} yet.
                  </td>
                </tr>
              )}
              {suffixes.map((s) => (
                <tr key={s.code} data-testid={`suffix-row-${s.code}`}>
                  <td className="font-mono-num font-semibold">{s.code}</td>
                  <td>{s.label}</td>
                  <td className="font-mono-num">{s.counselling_body}</td>
                  <td>
                    <button
                      data-testid={`delete-suffix-${s.code}`}
                      onClick={() => removeSuffix(s.code)}
                      className="text-slate-400 hover:text-rose-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
